import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowRight, ChevronDown } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { useFeatureFlags } from '@/admin/hooks/use-feature-flag'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { HOW_TO_SECTIONS, TOUR_CHAPTERS } from './tour-chapters'
import type { TourChapter } from './tour-chapters'
import { useTour } from './use-tour'

/**
 * "Show me how to": every platform module the user can open, in top-menu
 * order, each with its walkthroughs. The section for the page you are on
 * starts open. Clicking a walkthrough goes to its page (when needed) and
 * plays it there.
 */
export function ShowMeHowTo({ currentSection, onStart }: { currentSection?: string; onStart?: () => void }) {
  const { t } = useTranslation()
  const tour = useTour()
  const navigate = useNavigate()
  const location = useLocation()
  const featureFlags = useFeatureFlags()
  const { can } = usePermissions()
  const [open, setOpen] = useState<string | null>(currentSection ?? null)

  const sections = HOW_TO_SECTIONS.filter(
    (s) =>
      (!s.flags || s.flags.some((f) => featureFlags[f] === true)) &&
      (!s.anyPermission || s.anyPermission.some((p) => can(p))),
  ).map((s) => ({
    ...s,
    chapters: s.chapterIds
      .map((id) => TOUR_CHAPTERS.find((c) => c.id === id))
      .filter((c): c is TourChapter => !!c && (!c.flags || c.flags.some((f) => featureFlags[f] === true))),
  }))

  const start = (chapter: TourChapter) => {
    onStart?.()
    if (location.pathname === chapter.route.split('?')[0]) {
      tour.startChapter(chapter.id)
      return
    }
    navigate(chapter.route)
    setTimeout(() => tour.startChapter(chapter.id), 600)
  }

  return (
    <div data-testid="show-me-how-to">
      <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.13em] text-[var(--ink-3)]">
        {t('tour.howTo.title', 'Show me how to')}
      </p>
      <div className="flex flex-col divide-y-[0.5px] divide-[var(--rule)] rounded-[13px] border-[0.5px] border-[var(--rule)]">
        {sections.map((s) => {
          const isOpen = open === s.key
          const label = t(`tour.howTo.section.${s.key}`, s.label)
          return (
            <div key={s.key} data-testid={`how-to-section-${s.key}`}>
              <Button
                variant="ghost"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : s.key)}
                className="flex h-auto w-full items-center justify-between gap-2 rounded-none px-3 py-2.5 text-left text-[13px] font-medium text-[var(--ink)] hover:bg-[var(--bg-subtle)]"
              >
                <span className="flex items-center gap-2">
                  {label}
                  {s.chapters.length === 0 && (
                    <Badge variant="outline" className="text-[10.5px] font-normal text-[var(--ink-3)]">
                      {t('tour.howTo.soon', 'Coming soon')}
                    </Badge>
                  )}
                </span>
                <ChevronDown
                  size={14}
                  className={cn('flex-none text-[var(--ink-3)] transition-transform', isOpen && 'rotate-180')}
                />
              </Button>
              {isOpen && (
                <div className="flex flex-col gap-1 px-2 pb-2.5">
                  {s.chapters.length === 0 && (
                    <p className="px-2 py-1.5 text-[12.5px] text-[var(--ink-2)]">
                      {t('tour.howTo.soonBody', {
                        defaultValue: 'Walkthroughs for {{module}} are on their way.',
                        module: label,
                      })}
                    </p>
                  )}
                  {s.chapters.map((c, i) => (
                    <Button
                      key={c.id}
                      variant="ghost"
                      data-testid={`assistant-how-${c.id}`}
                      onClick={() => start(c)}
                      className={cn(
                        'flex h-auto w-full items-center justify-start gap-2.5 whitespace-normal rounded-[11px] px-2 py-2 text-left text-[13px] font-normal text-[var(--ink)] transition-colors hover:bg-[var(--green-bg)]',
                        // The first walkthrough is the main one; the rest sit under it.
                        i === 0 && s.chapters.length > 1 && 'font-medium',
                        i > 0 && s.chapters.length > 1 && 'pl-7 text-[var(--ink-2)]',
                      )}
                    >
                      <span className="grid w-[15px] flex-none place-items-center text-[var(--green)]">
                        <ArrowRight size={13} />
                      </span>
                      {t(`tour.chapter.${c.id}.title`, c.title)}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
